import React, { useEffect, useRef } from 'react';
import { Chart } from 'chart.js/auto';

export default function MajorAnalytics({
  majors = [],
  loading = false,
  showTable = false,
}) {
  const chartRef = useRef(null);
  const doughnutRef = useRef(null);

  const formatName = (n) => (n ? String(n).trim() : 'Tidak diketahui');

  // ─────────────────────────────────────────────
  // PREPARE DATA
  // ─────────────────────────────────────────────
  const cleaned = majors
    .map((m) => ({
      name: m.name ?? 'Tidak diketahui',
      total: m.total_users ?? 0,
    }))
    .sort((a, b) => b.total - a.total);

  const totalAll = cleaned.reduce((a, b) => a + b.total, 0);
  const totalMajors = cleaned.length;

  // Top 10
  const top10 = cleaned.slice(0, 10);

  // Top 5 Contribution
  const top5Total = cleaned.slice(0, 5).reduce((a, b) => a + b.total, 0);
  const top5Percent = ((top5Total / totalAll) * 100).toFixed(1);

  // Pareto 80/20
  const top20pct = Math.ceil(totalMajors * 0.2);
  const paretoTotal = cleaned
    .slice(0, top20pct)
    .reduce((a, b) => a + b.total, 0);
  const paretoPercent = ((paretoTotal / totalAll) * 100).toFixed(1);

  // Tier Distribusi
  const tiers = {
    t1: cleaned.filter((x) => x.total > 100).length,
    t2: cleaned.filter((x) => x.total >= 50 && x.total <= 100).length,
    t3: cleaned.filter((x) => x.total >= 20 && x.total < 50).length,
    t4: cleaned.filter((x) => x.total < 20).length,
  };

  // ─────────────────────────────────────────────
  // CHART: TOP 10 HORIZONTAL BAR
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');

    const chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: top10.map((m) => m.name),
        datasets: [
          {
            label: 'Total Mahasiswa',
            data: top10.map((m) => m.total),
            backgroundColor: 'rgba(66,133,244,0.65)',
            borderRadius: 6,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { beginAtZero: true },
          y: { ticks: { autoSkip: false } },
        },
      },
    });

    return () => chart.destroy();
  }, [majors]);

  // ─────────────────────────────────────────────
  // DOUGHNUT: TIER DISTRIBUTION
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!doughnutRef.current) return;

    const ctx = doughnutRef.current.getContext('2d');

    const chart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: [
          'Tier 1 (>100)',
          'Tier 2 (50–100)',
          'Tier 3 (20–49)',
          'Tier 4 (1–19)',
        ],
        datasets: [
          {
            data: [tiers.t1, tiers.t2, tiers.t3, tiers.t4],
            backgroundColor: ['#4285F4', '#34A853', '#FBBC04', '#9aa0a6'],
          },
        ],
      },
      options: {
        cutout: '60%',
        plugins: { legend: { position: 'bottom' } },
      },
    });

    return () => chart.destroy();
  }, [majors]);

  // Early return for empty data (not loading)
  if (!loading && (!majors || majors.length === 0)) {
    return (
      <div className="text-muted small text-center py-4">
        <i className="bi bi-inbox fs-3 d-block mb-2 opacity-50"></i>
        Tidak ada data major untuk ditampilkan.
      </div>
    );
  }

  return (
    <div>
      {/* ───────────────────────────────────────────── */}
      {/* SUMMARY CARDS */}
      {/* ───────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        {/* Total Mahasiswa */}
        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="text-muted small">Total Mahasiswa</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <h2 className="fw-bold">{totalAll}</h2>
                <div className="text-muted small">Total major: {totalMajors}</div>
              </>
            )}
          </div>
        </div>

        {/* Top 5 Contribution */}
        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="text-muted small">Top 5 Contribution</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <h2 className="fw-bold">{top5Percent}%</h2>
                <div className="text-muted small">({top5Total} mahasiswa)</div>
              </>
            )}
          </div>
        </div>

        {/* Pareto */}
        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="text-muted small">Pareto (80%)</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <h2 className="fw-bold">{top20pct} major</h2>
                <div className="text-muted small">{paretoPercent}% dari total</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ───────────────────────────────────────────── */}
      {/* MAIN CHART + DONUT + INSIGHT */}
      {/* ───────────────────────────────────────────── */}
      <div className="row g-4 mb-4">
        {/* LEFT CHART */}
        <div className="col-lg-8">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="bi bi-bar-chart-steps me-2 text-primary"></i>
                Top 10 Major
              </h6>
              {loading ? (
                <div className="text-center py-5" style={{ height: 360 }}>
                  <div className="spinner-border text-primary" />
                  <p className="text-muted mt-3 mb-0">Memuat grafik...</p>
                </div>
              ) : (
                <>
                  <div style={{ height: 360 }}>
                    <canvas ref={chartRef} />
                  </div>
                  <p className="chart-caption">
                    <i className="bi bi-lightbulb text-warning me-1"></i>
                    Cara baca: makin panjang batang, makin banyak mahasiswa dari jurusan itu. Urutan sudah dari yang terbesar.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT DONUT + INSIGHT */}
        <div className="col-lg-4">
          {/* DISTRIBUSI TIER */}
          <div className="card shadow-sm rounded-4 border-0 mb-3">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="bi bi-pie-chart-fill me-2 text-success"></i>
                Distribusi Tier
              </h6>

              {loading ? (
                <div className="text-center py-5" style={{ height: 240 }}>
                  <div className="spinner-border text-success" />
                  <p className="text-muted mt-3 mb-0 small">Memuat distribusi...</p>
                </div>
              ) : (
                <>
                  <div style={{ height: 240 }}>
                    <canvas ref={doughnutRef}></canvas>
                  </div>
                  <p className="chart-caption">
                    <i className="bi bi-lightbulb text-warning me-1"></i>
                    Cara baca: jurusan dikelompokkan dari kontribusi terbesar (T1) ke terkecil (T4) berdasarkan jumlah mahasiswa.
                  </p>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {showTable && (
        <div className="card shadow-sm rounded-4 border-0 mb-4">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-table me-2 text-primary"></i>
              Semua Jurusan/Program Studi
            </h6>

            <div className="table-responsive" style={{ maxHeight: 420 }}>
              <table className="table table-striped table-hover small">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>Major/Program Studi</th>
                    <th style={{ width: 120 }}>Mahasiswa</th>
                  </tr>
                </thead>
                <tbody>
                  {majors.map((m, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td
                        style={{
                          maxWidth: 360,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {formatName(m.name)}
                      </td>
                      <td>{m.total_users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .chart-caption { font-size: 0.75rem; color: #6c757d; margin: 10px 0 0; }
      `}</style>
    </div>
  );
}
